import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Icons } from './Icons';

const SANS  = '"Geist", "Inter", system-ui, sans-serif';
const BRAND = '#5A38FD';
const BRAND_L = '#a78bfa';
const GRAD  = 'linear-gradient(135deg, #5A38FD, #7F00FF)';
const S1    = '#08080D';
const S2    = '#0F0F17';
const BORDER = 'rgba(255,255,255,0.08)';
const TEXT   = 'rgba(255,255,255,0.9)';
const TEXT2  = 'rgba(255,255,255,0.5)';
const TEXT3  = 'rgba(255,255,255,0.28)';

const stepsConfig = [
  {
    id: 'dashboard',
    title: 'Welcome to NILVISION',
    description: 'Your command center. Track your NIL Score and earnings at a glance, monitor deal activity, and jump to any tool instantly.',
    icon: 'Home',
    color: BRAND,
    goTo: 'dashboard',
  },
  {
    id: 'nilscore',
    title: 'Your NIL Score',
    description: 'See your athlete market value broken down by earnings, visibility, profile, and reputation. Compare anonymously on the leaderboard and get AI-generated tips to climb.',
    icon: 'Star',
    color: BRAND_L,
    goTo: 'nilscore',
  },
  {
    id: 'analyzer',
    title: 'Contract Analyzer',
    description: 'Upload any NIL contract and get an instant Deal Safety Score — red flags, exclusivity traps, and negotiation suggestions surfaced in seconds. Ghost Agent pre-analyzes it before you even ask.',
    icon: 'Contract',
    color: BRAND_L,
    goTo: 'analyzer',
  },
  {
    id: 'ghost',
    title: 'Ghost Agent',
    description: 'Your AI negotiation teammate. Ask strategy questions, draft counter-offers, and plan your next deal in a private chat — with full context from your contracts and earnings.',
    icon: 'Ghost',
    color: BRAND_L,
    goTo: 'ghost',
  },
  {
    id: 'scheduler',
    title: 'Post Scheduler',
    description: 'Generate on-brand captions for any platform with AI, add school-specific hashtags, and schedule posts directly to Instagram — all in one flow.',
    icon: 'Calendar',
    color: BRAND_L,
    goTo: 'scheduler',
  },
  {
    id: 'earnings',
    title: 'Earnings Tracker',
    description: 'Log every NIL deal — brand, amount, date, and type. Each entry boosts your NIL Score, triggers a contract audit check, and builds your full financial picture.',
    icon: 'Earnings',
    color: '#4ADE80',
    goTo: 'earnings',
  },
  {
    id: 'scanner',
    title: 'Social Media Scanner',
    description: 'Scan Instagram, X, and TikTok for posts that could put your NIL deals at risk. Catch compliance issues before brands do — the agent watches continuously.',
    icon: 'Scanner',
    color: BRAND_L,
    goTo: 'scanner',
  },
  {
    id: 'profile',
    title: 'Your Profile',
    description: 'Fill in your sport, school, bio, and social handles. A complete profile unlocks higher NIL Score potential and gives the AI better context for every recommendation.',
    icon: 'Profile',
    color: BRAND_L,
    goTo: 'profile',
  },
];

const Tutorial = ({ isOpen, onClose, onComplete, setActiveComponent }) => {
  const { apiCall } = useAuth();
  const steps = useMemo(() => stepsConfig, []);
  const [index, setIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const total = steps.length;

  useEffect(() => {
    if (!isOpen) return;
    const step = steps[index];
    if (step?.goTo && typeof setActiveComponent === 'function') {
      setActiveComponent(step.goTo);
    }
  }, [index, isOpen, setActiveComponent, steps]);

  useEffect(() => {
    if (index >= total) setIndex(total - 1);
  }, [total, index]);

  const handleSkip = async () => {
    try { await apiCall('/api/user/complete-tutorial', { method: 'POST' }); } catch {}
    onClose?.();
    onComplete?.();
  };

  const handleNext = async () => {
    if (index < total - 1) {
      setAnimKey(k => k + 1);
      setIndex(i => i + 1);
    } else {
      try { await apiCall('/api/user/complete-tutorial', { method: 'POST' }); } catch {}
      onComplete?.();
    }
  };

  const handleBack = () => {
    if (index > 0) {
      setAnimKey(k => k + 1);
      setIndex(i => i - 1);
    }
  };

  if (!isOpen) return null;

  const step = steps[index];
  const IconComp = Icons[step.icon] || Icons.Home;
  const isLast = index === total - 1;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 60,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
    }}>
      <div style={{
        width: '100%', maxWidth: 520,
        background: S1,
        border: `1px solid rgba(90,56,253,0.2)`,
        borderRadius: 20, overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(90,56,253,0.12)',
        animation: 'nvTutSlideUp 0.3s cubic-bezier(0.25,0.46,0.45,0.94) forwards',
        fontFamily: SANS,
      }}>

        {/* Top hairline */}
        <div style={{ height: 1, background: `linear-gradient(90deg, transparent, rgba(167,139,250,0.5), transparent)` }} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem 0.875rem',
          borderBottom: `1px solid ${BORDER}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12, flexShrink: 0,
              background: `${step.color}20`,
              border: `1px solid ${step.color}40`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconComp style={{ width: 20, height: 20, color: step.color }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: 700, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: SANS }}>
                Step {index + 1} of {total}
              </p>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: TEXT, fontFamily: SANS }}>
                {step.title}
              </h3>
            </div>
          </div>
          <button
            onClick={handleSkip}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT3, padding: 6, borderRadius: 8, lineHeight: 0 }}
          >
            <Icons.Close style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem' }}>
          <p
            key={animKey}
            style={{
              margin: '0 0 1.25rem', color: TEXT2, fontSize: '0.88rem',
              lineHeight: 1.68, fontFamily: SANS,
              animation: 'nvTutFade 0.25s ease forwards',
            }}
          >
            {step.description}
          </p>

          {/* Progress pills */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            {steps.map((_, i) => (
              <div
                key={i}
                onClick={() => { if (i !== index) { setAnimKey(k => k + 1); setIndex(i); } }}
                style={{
                  height: 4, borderRadius: 2,
                  width: i === index ? 24 : 6,
                  background: i === index ? BRAND : BORDER,
                  transition: 'width 0.2s ease, background 0.2s ease',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.875rem 1.25rem',
          paddingBottom: 'calc(0.875rem + env(safe-area-inset-bottom, 0px))',
          borderTop: `1px solid ${BORDER}`,
          background: S2,
        }}>
          <button
            onClick={handleSkip}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT3, fontSize: '0.85rem', padding: '0.375rem 0', fontFamily: SANS }}
          >
            Skip tour
          </button>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleBack}
              disabled={index === 0}
              style={{
                fontSize: '0.85rem', padding: '0.5rem 1rem',
                border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.06)',
                color: TEXT2, borderRadius: 10, cursor: index === 0 ? 'not-allowed' : 'pointer',
                opacity: index === 0 ? 0.4 : 1, fontFamily: SANS, fontWeight: 600,
              }}
            >
              Back
            </button>
            <button
              onClick={handleNext}
              style={{
                fontSize: '0.85rem', padding: '0.5rem 1.25rem', minWidth: 80,
                border: 'none', background: GRAD, color: '#fff',
                borderRadius: 10, cursor: 'pointer', fontFamily: SANS, fontWeight: 700,
                boxShadow: '0 4px 16px rgba(90,56,253,0.4)',
              }}
            >
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes nvTutSlideUp {
          from { transform: scale(0.94) translateY(8px); opacity: 0; }
          to   { transform: scale(1)    translateY(0);   opacity: 1; }
        }
        @keyframes nvTutFade {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </div>
  );
};

export default Tutorial;
